import { useAtomValue } from 'jotai';

import useAuth from '@/hooks/useAuth';

import Constants from '@/constants';

import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';

export interface UseUserLocationsOptions {
  /**
   * When true, returns all licensee locations instead of the selected one.
   * Used for licensee-specific views that need all locations.
   * @default false
   */
  isLicenseeMode?: boolean;
}

// @TODO: Rename (e.g. useLeaderOrLicenseeLocations) to distinguish from
// auth.assignableTrainerLocations which is trainer+ scoped.
/**
 * Hook to get user-level locations for API queries.
 *
 * For non-licensee users: Returns all locations with at least leader permissions.
 * For licensee users/staff: Returns the selected location from the picker.
 * For licensee mode: Returns all licensee locations.
 *
 * @example
 * ```tsx
 * const userLocations = useUserLocations();
 *
 * const { data } = useQuery({
 *   ...getPlansOptions({ query: { locationNumbers: userLocations } }),
 *   enabled: userLocations.length > 0,
 * });
 * ```
 */
export const useUserLocations = ({
  isLicenseeMode = false,
}: UseUserLocationsOptions = {}): string[] => {
  const { data: auth } = useAuth();
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);

  const isLicenseeUser = auth?.isLicensee ?? false;
  const userIsLicenseeStaff = auth?.userIsLicenseeStaff ?? false;
  const userLicenseeLocations = auth?.licenseeLocations ?? [];
  const locationsWithAtLeastLeaderPermissions =
    auth?.allLocationsWithAtLeastLeaderPermissions ?? [];

  const isLicenseeUserOrStaff = userIsLicenseeStaff || isLicenseeUser;

  let result: string[] = [];
  let context = '';

  if (isLicenseeMode) {
    context = 'licenseeMode';
    result = userLicenseeLocations
      .map(location => location.number)
      .filter(
        (num): num is string =>
          num !== undefined &&
          num !== '' &&
          num !== Constants.SUPPORT_STAFF_LOCATION,
      );
  } else if (isLicenseeUserOrStaff) {
    context = 'licenseeUserOrStaff';
    const locationNumber = activeLicenseeLocation?.number;
    if (locationNumber && locationNumber !== Constants.SUPPORT_STAFF_LOCATION) {
      result = [locationNumber];
    }
  } else {
    result = locationsWithAtLeastLeaderPermissions;
  }

  if (result.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(
        `[useUserLocations] Falling back to locationsWithAtLeastLeaderPermissions`,
        {
          context,
          userId: auth?.userId,
          activeLicenseeLocation: activeLicenseeLocation?.number,
          userLicenseeLocationsCount: userLicenseeLocations.length,
          locationsWithAtLeastLeaderPermissions,
        },
      );
    }
    return locationsWithAtLeastLeaderPermissions;
  }

  return result;
};

export default useUserLocations;
